import { HttpContext } from '@angular/common/http'
import { STILLE_ABFRAGE } from '@saniup-webapps/shared'

/**
 * Abstände zwischen zwei Abfragen (nicht Zeitpunkte ab Öffnen) — sequenziell abgearbeitet, die
 * nächste Stufe startet also erst nach der Antwort der vorigen. Dicht in den ersten Sekunden, weil
 * sich dort das meiste bewegt, danach immer seltener. Summe = exakt 60 s: was länger braucht, holt
 * sich die Nutzer:in über den Reload-Button (bewusste Entscheidung, kein Dauer-Polling).
 */
export const VERSORGUNG_POLL_DELAYS_MS = [1_000, 1_000, 1_000, 2_000, 2_000, 3_000, 4_000, 6_000, 9_000, 13_000, 18_000]

/**
 * Kontext fuer die Abfragen einer Leiter: ihre Fehler schluckt die Leiter selbst, also gehoeren sie
 * nicht in die Fehlerzentrale. Ohne das landete dort jeder 404, den eine Stufe auf eine gerade
 * geloeschte Versorgung abbekommt — der `catch` unten kommt zu spaet, der Interceptor hat da schon
 * eingetragen.
 */
export function stilleAbfrage(): HttpContext {
  return new HttpContext().set(STILLE_ABFRAGE, true)
}

export interface VersorgungPollOptions<T> {
  /** Aktuell angezeigter Stand; `undefined`, solange die Erstladung noch läuft. */
  current: () => T | undefined
  /** Lädt den Stand neu. */
  load: () => Promise<T>
  /** `false`, sobald weggewechselt oder die Ansicht zerstört wurde — bricht die Leiter ab. */
  isActive: () => boolean
  /**
   * Woran sich ablesen lässt, dass sich etwas geändert hat. Verglichen wird mit `!==`, der
   * Rückgabewert muss also ein Primitiv sein.
   *
   * Die Versorgung selbst führt dafür `lastModifiedAt`; ein zusammengesetztes DTO ohne eigenen
   * Änderungsstempel (der Abrechnungs-Tab) gibt hier seinen ganzen Rumpf als JSON zurück. Bewusst
   * keine Teilsignatur aus „den wichtigen Feldern": die nächste Ergänzung am DTO fiele sonst
   * stillschweigend heraus, und die Leiter meldete eine Änderung nicht, die längst da ist.
   */
  signatur: (wert: T) => unknown
  /** Wird nur gerufen, wenn sich die Signatur tatsächlich geändert hat. */
  onUpdate: (fresh: T) => void
}

/**
 * Stille Nachlade-Leiter: Am angezeigten Stand arbeitet das Backend weiter, nachdem die Ansicht ihn
 * geholt hat — direkt nach der Anlage am EKV, später an Genehmigung, Logistik und Lieferungen, und
 * nach jeder Buchung, deren Wirkung über den Service Bus im Deamon entsteht (Abrechnungsposten,
 * Patientenrechnung).
 *
 * Läuft deshalb unabhängig vom Status immer die volle Leiter durch. Aktualisiert wird ohne
 * Ladezustand und nur bei geänderter Signatur; transiente Fehler werden geschluckt und auf der
 * nächsten Stufe erneut versucht.
 */
export async function pollVersorgungUpdates<T>({ current, load, isActive, signatur, onUpdate }: VersorgungPollOptions<T>): Promise<void> {
  for (const delayMs of VERSORGUNG_POLL_DELAYS_MS) {
    await sleep(delayMs)
    if (!isActive()) return
    // Erstladung läuft noch — diese Stufe überspringen statt gegen einen leeren Stand zu vergleichen.
    const aktuell = current()
    if (aktuell === undefined) continue

    try {
      const fresh = await load()
      if (!isActive()) return
      const vorher = current()
      if (vorher === undefined || signatur(fresh) !== signatur(vorher)) {
        onUpdate(fresh)
      }
    } catch {
      // Stille Aktualisierung — transiente Fehler ignorieren, die nächste Stufe versucht es erneut.
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
